import { useCallback, useEffect, useRef, useState } from "react";
import { controlToastState } from "risa-oasis-ui_v2";
import { mergePdfs, PdfInfo } from "../api/postCall/mergePdfs";
import { logError } from "../utils/customLogger";

interface UseMergePdfsReturn {
  mergedFile: File | null;
  mergedPdfToRender: File | null;
  isMerging: boolean;
  mergeError: string | null;
  mergePdfsFromBase64: (
    base64Urls: string[],
    fileName?: string,
  ) => Promise<void>;
  mergePdfsFromInfo: (pdfInfo: PdfInfo[], fileName?: string) => Promise<void>;
  clearMergedFile: () => void;
}

/**
 * Custom hook to merge multiple PDFs into a single PDF
 * @returns Object containing merged file, loading state, error, and utility functions
 */
export const useMergePdfs = (): UseMergePdfsReturn => {
  const [mergedFile, setMergedFile] = useState<File | null>(null);
  const [mergedPdfToRender, setMergedPdfToRender] = useState<File | null>(null);
  const [isMerging, setIsMerging] = useState<boolean>(false);
  const [mergeError, setMergeError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Cleanup: abort any pending requests when component unmounts
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const formatBase64ForApi = useCallback((base64Url: string): string => {
    const base64Data = base64Url.includes(",")
      ? base64Url.split(",")[1]
      : base64Url;
    return `file://${base64Data}`;
  }, []);

  const blobToFile = useCallback((blob: Blob, fileName: string): File => {
    try {
      const file = new File([blob], fileName, { type: "application/pdf" });
      return file;
    } catch (error) {
      logError(error as Error, "blobToFile conversion error");
      throw error;
    }
  }, []);

  const mergePdfsFromBase64 = useCallback(
    async (base64Urls: string[], fileName: string = "merged-document.pdf") => {
      if (!base64Urls || base64Urls.length === 0) {
        setMergeError("No PDFs provided to merge");
        controlToastState("merge-pdfs-error");
        return;
      }

      // Cancel any existing request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller
      abortControllerRef.current = new AbortController();
      const signal = abortControllerRef.current.signal;

      setIsMerging(true);
      setMergeError(null);

      try {
        const pdfInfo = base64Urls.map((base64Url, index) => ({
          source: formatBase64ForApi(base64Url),
          order: (index + 1).toString(),
        }));

        const response = await mergePdfs(pdfInfo, signal);

        if (!response || !response.blob) {
          throw new Error("Failed to merge PDFs - no blob in response");
        }

        try {
          const mergedPdfFile = blobToFile(response.blob, fileName);
          setMergedFile(mergedPdfFile);
          setMergedPdfToRender(mergedPdfFile);
          controlToastState("merge-pdfs-success");
        } catch (convertError) {
          logError(convertError as Error, "Error converting blob to file");
          throw new Error("Failed to convert merged PDF blob to file");
        }

        // Clear the abort controller reference on success
        abortControllerRef.current = null;
      } catch (err) {
        if (err instanceof Error && err.name === "AbortError") {
          // Request was cancelled, don't show error toast
          console.log("Merge PDFs request was cancelled");
        } else {
          const errorMessage = "Failed to merge PDFs. Please try again.";
          setMergeError(errorMessage);
          logError(err as Error, "useMergePdfs - mergePdfsFromBase64");
          controlToastState("merge-pdfs-error");
        }
        // Clear the abort controller reference on error
        abortControllerRef.current = null;
      } finally {
        setIsMerging(false);
      }
    },
    [formatBase64ForApi, blobToFile],
  );

  const mergePdfsFromInfo = useCallback(
    async (pdfInfo: PdfInfo[], fileName: string = "merged-document.pdf") => {
      if (!pdfInfo || pdfInfo.length === 0) {
        setMergeError("No PDFs provided to merge");
        controlToastState("merge-pdfs-error");
        return;
      }

      // Cancel any existing request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller
      abortControllerRef.current = new AbortController();
      const signal = abortControllerRef.current.signal;

      setIsMerging(true);
      setMergeError(null);

      try {
        const response = await mergePdfs(pdfInfo, signal);

        if (!response || !response.blob) {
          throw new Error("Failed to merge PDFs - no blob in response");
        }

        try {
          const mergedPdfFile = blobToFile(response.blob, fileName);
          setMergedFile(mergedPdfFile);
          setMergedPdfToRender(mergedPdfFile);
          controlToastState("merge-pdfs-success");
        } catch (convertError) {
          logError(convertError as Error, "Error converting blob to file");
          throw new Error("Failed to convert merged PDF blob to file");
        }

        // Clear the abort controller reference on success
        abortControllerRef.current = null;
      } catch (err) {
        if (err instanceof Error && err.name === "AbortError") {
          // Request was cancelled, don't show error toast
          console.log("Merge PDFs request was cancelled");
        } else {
          const errorMessage = "Failed to merge PDFs. Please try again.";
          setMergeError(errorMessage);
          logError(err as Error, "useMergePdfs - mergePdfsFromInfo");
          controlToastState("merge-pdfs-error");
        }
        // Clear the abort controller reference on error
        abortControllerRef.current = null;
      } finally {
        setIsMerging(false);
      }
    },
    [blobToFile],
  );

  const clearMergedFile = useCallback(() => {
    setMergedFile(null);
    setMergedPdfToRender(null);
    setMergeError(null);
  }, []);

  return {
    mergedFile,
    mergedPdfToRender,
    isMerging,
    mergeError,
    mergePdfsFromBase64,
    mergePdfsFromInfo,
    clearMergedFile,
  };
};
